import { useEffect, useRef } from 'react';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { PostCardSkeleton } from '@/components/skeletons/PostCardSkeleton';
import { PostCard } from '@/features/posts/components/PostCard';
import type { Post } from '@/lib/schemas/post';

interface FeedListProps {
  posts: Post[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onTagClick?: (tag: string) => void;
  onDelete?: (post: Post) => void;
}

/**
 * The feed, with infinite scroll and a real "Load more" button behind it
 * (spec §6.2).
 *
 * The button is not a fallback for old browsers — it is the accessible path.
 * An IntersectionObserver that only fires on scroll is unreachable by keyboard
 * and invisible to a screen reader, so the button is always in the DOM and
 * always focusable; the observer just clicks it for people who are scrolling.
 */
export function FeedList({
  posts,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onTagClick,
  onDelete,
}: FeedListProps) {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement>(null);

  // The observer reads the current callback and fetch state through a ref
  // rather than through its dependency array.
  //
  // This is load-bearing, not a micro-optimisation: `onLoadMore` and
  // `isFetchingNextPage` change on every render, so depending on them would
  // tear down and re-create the observer each time. A freshly-created observer
  // fires immediately for an already-intersecting target, which triggers
  // another fetch, another render, another observer — an infinite loop that
  // React eventually reports as "Maximum update depth exceeded".
  const latest = useRef({ onLoadMore, isFetchingNextPage });
  latest.current = { onLoadMore, isFetchingNextPage };

  // A synchronous latch, separate from `isFetchingNextPage`.
  //
  // React state only reflects a fetch on the *next* render, so several
  // intersections firing in the same tick would all read `isFetchingNextPage`
  // as false and each request the same cursor — the reader would see the same
  // page appended two or three times. This closes immediately.
  const requestInFlight = useRef(false);

  useEffect(() => {
    if (!isFetchingNextPage) requestInFlight.current = false;
  }, [isFetchingNextPage]);

  useEffect(() => {
    const element = sentinelRef.current;
    if (!element || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (requestInFlight.current || latest.current.isFetchingNextPage) return;

        requestInFlight.current = true;
        latest.current.onLoadMore();
      },
      // Start fetching before the sentinel is actually visible, so the next
      // page is usually there by the time the reader reaches it.
      { rootMargin: '600px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasNextPage]);

  return (
    <Stack spacing={2}>
      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          {...(onTagClick ? { onTagClick } : {})}
          {...(onDelete ? { onDelete } : {})}
        />
      ))}

      {isFetchingNextPage && <PostCardSkeleton />}

      <div ref={sentinelRef} aria-hidden />

      {hasNextPage ? (
        <Button
          onClick={onLoadMore}
          disabled={isFetchingNextPage}
          variant="outlined"
          sx={{ alignSelf: 'center' }}
          startIcon={
            isFetchingNextPage ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {isFetchingNextPage ? t('common.loading') : t('feed.loadMore')}
        </Button>
      ) : (
        posts.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            That&apos;s everything.
          </Typography>
        )
      )}
    </Stack>
  );
}
